
import React, { useState, useEffect } from "react";
import { ClipboardList, Save } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { 
  pricingFormSchema, 
  PricingFormValues, 
  commonCurrencies 
} from "../schemas/pricingFormSchema";
import { 
  summaryFormSchema,
  SummaryFormValues 
} from "../schemas/summaryFormSchema";
import {
  questionsFormSchema,
  QuestionsFormValues
} from "../schemas/questionsFormSchema";
import {
  reverseAuctionFormSchema,
  ReverseAuctionFormValues,
  timezoneOptions,
  bidderVisibilityOptions,
  auctionDurationOptions
} from "../schemas/reverseAuctionFormSchema";

export const SummaryReviewForm = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  // Store form data from localStorage
  const [summaryData, setSummaryData] = useState<SummaryFormValues | null>(null);
  const [pricingData, setPricingData] = useState<PricingFormValues | null>(null);
  const [questionsData, setQuestionsData] = useState<QuestionsFormValues | null>(null);
  const [reverseAuctionData, setReverseAuctionData] = useState<ReverseAuctionFormValues | null>(null);
  
  // Initialize form instances with their respective schemas
  const summaryForm = useForm<SummaryFormValues>({
    resolver: zodResolver(summaryFormSchema),
  });
  
  const pricingForm = useForm<PricingFormValues>({
    resolver: zodResolver(pricingFormSchema),
  });
  
  // Load all data from localStorage on component mount and when localStorage changes
  useEffect(() => {
    // Function to load all form data from localStorage
    const loadAllFormData = () => {
      // Load summary data
      const savedSummaryData = localStorage.getItem("proposal-summary");
      if (savedSummaryData) {
        try {
          const parsedData = JSON.parse(savedSummaryData);
          setSummaryData(parsedData);
        } catch (error) {
          console.error("Error parsing saved summary data:", error);
        }
      }
      
      // Load pricing data
      const savedPricingData = localStorage.getItem("proposal-pricing");
      if (savedPricingData) {
        try {
          const parsedData = JSON.parse(savedPricingData);
          setPricingData(parsedData);
        } catch (error) {
          console.error("Error parsing saved pricing data:", error);
        }
      }
      
      // Load questions data
      const savedQuestionsData = localStorage.getItem("proposal-questions");
      if (savedQuestionsData) {
        try {
          const parsedData = JSON.parse(savedQuestionsData);
          setQuestionsData(parsedData);
        } catch (error) {
          console.error("Error parsing saved questions data:", error);
        }
      }
      
      // Load reverse auction data
      const savedReverseAuctionData = localStorage.getItem("proposal-reverse-auction");
      if (savedReverseAuctionData) {
        try {
          const parsedData = JSON.parse(savedReverseAuctionData);
          setReverseAuctionData(parsedData);
        } catch (error) {
          console.error("Error parsing saved reverse auction data:", error);
        }
      }
    };
    
    // Load data initially
    loadAllFormData();
    
    // Setup event listener for storage changes
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key && e.key.startsWith('proposal-')) {
        loadAllFormData();
      }
    };
    
    // Add event listener for storage changes
    window.addEventListener('storage', handleStorageChange);
    
    // Setup polling to detect changes within the same window
    const intervalId = setInterval(loadAllFormData, 1000);
    
    // Cleanup event listener and interval
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(intervalId);
    };
  }, []);
  
  // Get the selected currency display name
  const getSelectedCurrencyDisplay = () => {
    if (!pricingData) return "No currency selected";
    
    const selectedCurrency = commonCurrencies.find(c => c.value === pricingData.currency);
    return selectedCurrency 
      ? selectedCurrency.label 
      : pricingData.customCurrency || "Custom currency";
  };

  // Get currency symbol
  const getCurrencySymbol = () => {
    if (!pricingData?.currency) return "$";
    const currency = commonCurrencies.find(c => c.value === pricingData.currency);
    return currency?.symbol || "$";
  };
  
  // Format the price arrangement for display
  const getPricingArrangementDisplay = () => {
    if (!pricingData || !pricingData.priceArrangement) return "Not specified";
    
    const mapping: Record<string, string> = {
      "fixed-fee": "Fixed Fee",
      "price-cap": "Price Cap",
      "billable-hour": "Billable Hour",
      "other": "Other"
    };
    
    return mapping[pricingData.priceArrangement] || pricingData.priceArrangement;
  };

  // Get estimate display
  const getEstimateDisplay = () => {
    if (!pricingData?.provideEstimate) return null;
    
    const symbol = getCurrencySymbol();
    
    if (pricingData.estimateType === "fixed" && pricingData.estimatePrice) {
      return `${symbol}${Number(pricingData.estimatePrice).toLocaleString()}`;
    }
    
    if (pricingData.estimateType === "range" && (pricingData.estimatePriceMin || pricingData.estimatePriceMax)) {
      const min = Number(pricingData.estimatePriceMin || 0).toLocaleString();
      const max = Number(pricingData.estimatePriceMax || 0).toLocaleString();
      return `${symbol}${min} - ${symbol}${max}`;
    }
    
    return null;
  };
  
  // Format the questions for display
  const getQuestionsDisplay = () => {
    if (!questionsData || !questionsData.questions || questionsData.questions.length === 0) {
      return "No questions have been added";
    }
    
    return questionsData.questions.map((q, index) => (
      <div key={index} className="mb-3 pb-2 border-b border-gray-100 last:border-0">
        <div className="font-medium">Question {index + 1}: {q.question}</div>
        {q.explanation && (
          <p className="text-sm text-gray-600 mt-1">Explanation: {q.explanation}</p>
        )}
        <p className="text-xs text-gray-500 mt-1">
          {q.isRequired ? "Required" : "Optional"}
        </p>
      </div>
    ));
  };
  
  // Format date for display
  const formatDate = (dateString?: string | Date) => {
    if (!dateString) return "Not specified";
    try {
      return new Date(dateString).toLocaleString();
    } catch (error) {
      return String(dateString);
    }
  };

  // Get auction duration display
  const getAuctionDurationDisplay = () => {
    if (!reverseAuctionData || !reverseAuctionData.auctionDuration) return "Not specified";
    
    if (reverseAuctionData.auctionDuration === "custom") {
      const weeks = reverseAuctionData.customWeeks || 0;
      const days = reverseAuctionData.customDays || 0;
      const hours = reverseAuctionData.customHours || 0;
      
      const parts = [];
      if (weeks > 0) parts.push(`${weeks} week${weeks !== 1 ? 's' : ''}`);
      if (days > 0) parts.push(`${days} day${days !== 1 ? 's' : ''}`);
      if (hours > 0) parts.push(`${hours} hour${hours !== 1 ? 's' : ''}`);
      
      return parts.length > 0 ? parts.join(', ') : "0 hours";
    } else {
      const option = auctionDurationOptions.find(o => o.value === reverseAuctionData.auctionDuration);
      return option ? option.label : reverseAuctionData.auctionDuration;
    }
  };

  // Get timezone display
  const getTimezoneDisplay = () => {
    if (!reverseAuctionData || !reverseAuctionData.timezone) return "Not specified";
    
    const option = timezoneOptions.find(o => o.value === reverseAuctionData.timezone);
    return option ? option.label : reverseAuctionData.timezone;
  };

  // Get bidder visibility display
  const getBidderVisibilityDisplay = () => {
    if (!reverseAuctionData || !reverseAuctionData.bidderVisibility) return "Not specified";
    
    const option = bidderVisibilityOptions.find(o => o.value === reverseAuctionData.bidderVisibility);
    return option ? option.label : reverseAuctionData.bidderVisibility;
  };
  
  const handleBackClick = () => {
    // Navigate back to the last active section
    const savedSection = localStorage.getItem("proposal-active-section") || "summary";
    navigate("/new");
  };
  
  const handleProceedClick = () => {
    // Set the active section to "invite" and force a page reload to apply the change
    localStorage.setItem("proposal-active-section", "invite");
    
    // Use window.location to force a complete reload so the new section becomes active
    window.location.href = "/new";
  };

  const handleSaveTemplate = () => {
    // Save all form data as a template
    if (!summaryData || !summaryData.title) {
      toast({
        title: "Missing title",
        description: "Please provide a title for your template in the Summary section.",
        variant: "destructive"
      });
      return;
    }

    // Get existing templates or initialize empty array
    const existingTemplates = JSON.parse(localStorage.getItem("saved-templates") || "[]");
    
    // Create new template object
    const newTemplate = {
      id: Date.now().toString(),
      title: summaryData.title,
      dateCreated: new Date().toISOString(),
      summaryData,
      pricingData,
      questionsData,
      reverseAuctionData
    };
    
    // Add new template to array
    existingTemplates.push(newTemplate);
    
    // Save updated array back to localStorage
    localStorage.setItem("saved-templates", JSON.stringify(existingTemplates));
    
    // Show success toast
    toast({
      title: "Template saved",
      description: `"${summaryData.title}" has been saved to your templates.`
    });
  };

  return (
    <div className="transition-opacity">
      <div className="flex items-center mb-4">
        <ClipboardList className="h-6 w-6 text-black mr-2" />
        <h2 className="text-xl font-semibold text-gray-800">Review</h2>
      </div>
      <p className="text-gray-600 mb-6">Review all sections of your RFP/Instructions before finalizing</p>

      <Card className="mb-6">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">RFP/Instructions Overview</CardTitle>
        </CardHeader>
        <CardContent>
          <Accordion type="multiple" defaultValue={["summary", "pricing", "questions", "reverse-auction"]} className="w-full">
            <AccordionItem value="summary">
              <AccordionTrigger className="text-base font-medium">
                <div className="flex items-center justify-between w-full pr-2">
                  <span>Summary</span>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={(e) => {
                      e.stopPropagation();
                      localStorage.setItem("proposal-active-section", "summary");
                      window.location.href = "/new";
                    }}
                    className="text-xs text-primary hover:text-primary/80"
                  >
                    Edit
                  </Button>
                </div>
              </AccordionTrigger>
              <AccordionContent>
                <div className="space-y-2 pl-2">
                  <div>
                    <span className="font-medium">Title:</span>
                    <p className="text-sm text-gray-600">{summaryData?.title || "No title provided"}</p>
                  </div>
                  <div>
                    <span className="font-medium">Matter Name:</span>
                    <p className="text-sm text-gray-600">{summaryData?.matterName || "No matter name provided"}</p>
                  </div>
                  <div>
                    <span className="font-medium">Region:</span>
                    <p className="text-sm text-gray-600">{summaryData?.region || "Not specified"}</p>
                  </div>
                  <div>
                    <span className="font-medium">Team:</span>
                    <p className="text-sm text-gray-600">{summaryData?.team || "Not specified"}</p>
                  </div>
                  <div>
                    <span className="font-medium">Description:</span>
                    <p className="text-sm text-gray-600">{summaryData?.description || "No description provided"}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="font-medium">Include company info:</span>
                    <span className="text-sm text-gray-600">
                      {summaryData?.includeCompanyInfo ? "Yes" : "No"}
                    </span>
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="pricing">
              <AccordionTrigger className="text-base font-medium">
                <div className="flex items-center justify-between w-full pr-2">
                  <span>Pricing</span>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={(e) => {
                      e.stopPropagation();
                      localStorage.setItem("proposal-active-section", "pricing");
                      window.location.href = "/new";
                    }}
                    className="text-xs text-primary hover:text-primary/80"
                  >
                    Edit
                  </Button>
                </div>
              </AccordionTrigger>
              <AccordionContent>
                <div className="space-y-2 pl-2">
                  <div>
                    <span className="font-medium">Pricing model:</span>
                    <p className="text-sm text-gray-600">{getPricingArrangementDisplay()}</p>
                  </div>
                  <div>
                    <span className="font-medium">Currency:</span>
                    <p className="text-sm text-gray-600">{getSelectedCurrencyDisplay()}</p>
                  </div>
                  {pricingData?.provideEstimate && (
                    <div>
                      <span className="font-medium">
                        {pricingData.estimateType === "range" ? "Estimated Price Range:" : "Estimated Price:"}
                      </span>
                      <p className="text-sm text-gray-600">{getEstimateDisplay()}</p>
                    </div>
                  )}
                </div>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="questions">
              <AccordionTrigger className="text-base font-medium">
                <div className="flex items-center justify-between w-full pr-2">
                  <span>Questions</span>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={(e) => {
                      e.stopPropagation();
                      localStorage.setItem("proposal-active-section", "questions");
                      window.location.href = "/new";
                    }}
                    className="text-xs text-primary hover:text-primary/80"
                  >
                    Edit
                  </Button>
                </div>
              </AccordionTrigger>
              <AccordionContent>
                <div className="space-y-2 pl-2">
                  {getQuestionsDisplay()}
                </div>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="reverse-auction">
              <AccordionTrigger className="text-base font-medium">
                <div className="flex items-center justify-between w-full pr-2">
                  <span>Reverse Auction</span>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={(e) => {
                      e.stopPropagation();
                      localStorage.setItem("proposal-active-section", "reverse-auction");
                      window.location.href = "/new";
                    }}
                    className="text-xs text-primary hover:text-primary/80"
                  >
                    Edit
                  </Button>
                </div>
              </AccordionTrigger>
              <AccordionContent>
                <div className="space-y-2 pl-2">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">Reverse auction enabled:</span>
                    <span className="text-sm text-gray-600">
                      {reverseAuctionData?.enableReverseAuction === "yes" ? "Yes" : "No"}
                    </span>
                  </div>
                  
                  {reverseAuctionData?.enableReverseAuction === "yes" && (
                    <>
                      <div>
                        <span className="font-medium">Timezone:</span>
                        <p className="text-sm text-gray-600">{getTimezoneDisplay()}</p>
                      </div>
                      <div>
                        <span className="font-medium">Proposal Due Date/Time:</span>
                        <p className="text-sm text-gray-600">
                          {formatDate(reverseAuctionData.proposalDueDate)}
                          {reverseAuctionData.proposalDueTime ? ` at ${reverseAuctionData.proposalDueTime}` : ""}
                        </p>
                      </div>
                      <div>
                        <span className="font-medium">Auction Start Date/Time:</span>
                        <p className="text-sm text-gray-600">
                          {formatDate(reverseAuctionData.auctionStartDate)}
                          {reverseAuctionData.auctionStartTime ? ` at ${reverseAuctionData.auctionStartTime}` : ""}
                        </p>
                      </div>
                      <div>
                        <span className="font-medium">Auction Duration:</span>
                        <p className="text-sm text-gray-600">{getAuctionDurationDisplay()}</p>
                      </div>
                      <div>
                        <span className="font-medium">Include Pricing Information:</span>
                        <p className="text-sm text-gray-600">
                          {reverseAuctionData.includePricing ? "Yes" : "No"}
                        </p>
                      </div>
                      <div>
                        <span className="font-medium">Notify Bidders When Outbid:</span>
                        <p className="text-sm text-gray-600">
                          {reverseAuctionData.notifyBidders ? "Yes" : "No"}
                        </p>
                      </div>
                      <div>
                        <span className="font-medium">Bidder Visibility:</span>
                        <p className="text-sm text-gray-600">{getBidderVisibilityDisplay()}</p>
                      </div>
                      {reverseAuctionData.additionalNotes && (
                        <div>
                          <span className="font-medium">Additional Notes:</span>
                          <p className="text-sm text-gray-600">{reverseAuctionData.additionalNotes}</p>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </CardContent>
      </Card>

      <div className="flex justify-end mt-6">
        <Button 
          type="button" 
          variant="outline" 
          className="mr-2" 
          onClick={handleBackClick}
        >
          Back to Editing
        </Button>
        <Button 
          type="button" 
          variant="outline" 
          className="mr-2"
          onClick={handleSaveTemplate}
        >
          <Save className="mr-2" />
          Save Template
        </Button>
        <Button type="button" onClick={handleProceedClick}>
          Proceed to Invite
        </Button>
      </div>
    </div>
  );
};
